import {CommentsCategory} from '@domain/comments'

import {Api} from './api'

import type {ShutdownStatus} from '@refra/modules/common/enums'
import {EditShutdownDTO, Shutdown, AddShutdownDTO} from '@refra/modules/common/types'
import {
  MateriaInstallationReportRequest,
  MateriaInstallationReportResponse
} from '@refra/modules/refra-flexible-report/types'
import type {
  AddMaterialInstallationDTO,
  EditMaterialInstallationDTO,
  DrillingZone,
  DrillingPosition,
  EditKilnReplacementDTO
} from '@refra/modules/refra-planning/types'
import {
  AddStaticPartDTO,
  ModifyStaticReplacementDTO,
  EditStaticPartDTO
} from '@refra/modules/refra-static-parts/types'

export type Mutations = typeof mutations

export const mutations = {
  addRefractoryDocuments: async ({
    refractoryId,
    documents
  }: {
    refractoryId: number
    documents: File[]
  }) => {
    const path = `/plants/refractory-product-data-catalog/refractories/${refractoryId}/documents`

    const formData = new FormData()
    documents.forEach((document) => formData.append('documents', document))

    await Api.axiosInstance.post<void>(path, formData, {
      headers: {
        'content-type': 'multipart/form-data'
      }
    })
  },
  deleteRefractoryDocument: async ({
    refractoryId,
    documentId
  }: {
    refractoryId: number
    documentId: number
  }) => {
    const path = `/plants/refractory-product-data-catalog/refractories/${refractoryId}/documents/${documentId}`

    await Api.axiosInstance.delete<void>(path)
  },
  updateRefractoryCustomization: async ({
    plantUpmId,
    refractoryId,
    color
  }: {
    plantUpmId: string
    refractoryId: number
    color: string | null
  }) => {
    const path = `/plants/${plantUpmId}/refractory-product-data-catalog/refractories/${refractoryId}/customization`

    await Api.axiosInstance.patch<void>(path, {
      color: color
    })
  },
  addShutdown: async ({
    plantUpmId,
    kilnUpmId,
    shutdownDTO
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownDTO: AddShutdownDTO
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns`

    await Api.axiosInstance.post<Shutdown>(path, shutdownDTO)
  },
  editShutdown: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId,
    shutdownDTO
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
    shutdownDTO: EditShutdownDTO
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}`

    await Api.axiosInstance.patch<void>(path, shutdownDTO)
  },
  editKilnReplacement: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId,
    kilnReplacementDTO
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
    kilnReplacementDTO: EditKilnReplacementDTO
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/kiln-replacement`

    await Api.axiosInstance.patch<void>(path, kilnReplacementDTO)
  },
  editDrillingSettings: async ({
    drillingZones,
    drillingPositions,
    plantUpmId,
    kilnUpmId
  }: {
    plantUpmId: string
    kilnUpmId: string
    drillingZones: DrillingZone[]
    drillingPositions: DrillingPosition[]
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/settings`

    await Api.axiosInstance.patch<void>(path, {drillingZones, drillingPositions})
  },
  editShutdownStatus: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId,
    status
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
    status: ShutdownStatus
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/shutdown-status`

    await Api.axiosInstance.patch<void>(path, {status})
  },
  addMaterialInstallation: async ({
    material,
    kilnUpmId,
    shutdownId,
    plantUpmId
  }: {
    kilnUpmId: string
    shutdownId: number
    plantUpmId: string
    material: AddMaterialInstallationDTO
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/kiln-replacement/material-installations`

    return await Api.axiosInstance.post<void>(path, material)
  },
  editMaterialInstallation: async ({
    material,
    kilnUpmId,
    shutdownId,
    plantUpmId,
    installationId
  }: {
    kilnUpmId: string
    shutdownId: number
    plantUpmId: string
    installationId: string
    material: EditMaterialInstallationDTO
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/kiln-replacement/material-installations/${installationId}`

    return await Api.axiosInstance.patch<void>(path, material)
  },
  deleteMaterialInstallation: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId,
    installationId
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
    installationId: number
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/kiln-replacement/material-installations/${installationId}`

    return await Api.axiosInstance.delete<void>(path)
  },
  onboardKiln: async ({plantUpmId, kilnUpmId}: {plantUpmId: string; kilnUpmId: string}) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/onboarding`

    return await Api.axiosInstance.post<void>(path)
  },
  addMaterialInstallationDocuments: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId,
    installationId,
    documents
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
    installationId: number
    documents: File[]
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/kiln-replacement/material-installations/${installationId}/documents`

    const formData = new FormData()
    documents.forEach((document) => formData.append('documents', document))

    await Api.axiosInstance.post<void>(path, formData, {
      headers: {
        'content-type': 'multipart/form-data'
      }
    })
  },
  deleteMaterialInstallationDocument: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId,
    installationId,
    documentId
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
    installationId: number
    documentId: number
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/kiln-replacement/material-installations/${installationId}/documents/${documentId}`

    await Api.axiosInstance.delete<void>(path)
  },
  addComment: async ({
    plantCode,
    commentsCategory,
    commentCategoryInstanceIds,
    comment
  }: {
    plantCode: string
    commentsCategory: CommentsCategory
    commentCategoryInstanceIds: string | number
    comment: string
  }) => {
    const path = `/comments/${plantCode}/${commentsCategory}/${commentCategoryInstanceIds}`
    await Api.axiosInstance.post<void>(path, {value: comment})
  },
  editComment: async ({
    plantCode,
    commentsCategory,
    commentCategoryInstanceIds,
    commentId,
    comment
  }: {
    plantCode: string
    commentsCategory: CommentsCategory
    commentCategoryInstanceIds: string | number
    commentId: number
    comment: string
  }) => {
    const path = `/comments/${plantCode}/${commentsCategory}/${commentCategoryInstanceIds}/${commentId}`
    await Api.axiosInstance.put<void>(path, {value: comment})
  },
  deleteComment: async ({
    plantCode,
    commentsCategory,
    commentCategoryInstanceIds,
    commentId
  }: {
    plantCode: string
    commentsCategory: CommentsCategory
    commentCategoryInstanceIds: string | number
    commentId: number
  }) => {
    const path = `/comments/${plantCode}/${commentsCategory}/${commentCategoryInstanceIds}/${commentId}`
    await Api.axiosInstance.delete<void>(path)
  },
  addStaticPart: async ({
    plantUpmId,
    kilnUpmId,
    staticPartDTO
  }: {
    plantUpmId: string
    kilnUpmId: string
    staticPartDTO: AddStaticPartDTO
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/static-parts`

    await Api.axiosInstance.post<Shutdown>(path, staticPartDTO)
  },
  editStaticPart: async ({
    plantUpmId,
    kilnUpmId,
    staticPartId,
    staticPartDTO
  }: {
    plantUpmId: string
    kilnUpmId: string
    staticPartId: number
    staticPartDTO: EditStaticPartDTO
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/static-parts/${staticPartId}`

    await Api.axiosInstance.patch<Shutdown>(path, staticPartDTO)
  },
  setUserStorageValue: async ({keyIdentifier, value}: {keyIdentifier: string; value: string}) => {
    const path = '/distributeduserstorage'
    await Api.axiosInstance.post(path, {keyIdentifier, value})
  },
  addStaticPartReplacement: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId,
    staticPartReplacementDTO
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
    staticPartReplacementDTO: ModifyStaticReplacementDTO
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/static-replacements`

    await Api.axiosInstance.post<Shutdown>(path, staticPartReplacementDTO)
  },
  editStaticPartReplacement: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId,
    replacementId,
    staticPartReplacementDTO
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
    replacementId: number
    staticPartReplacementDTO: ModifyStaticReplacementDTO
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/static-replacements/${replacementId}`

    await Api.axiosInstance.patch<Shutdown>(path, staticPartReplacementDTO)
  },
  removeStaticPartReplacement: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId,
    replacementId
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
    replacementId: number
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/static-replacements/${replacementId}`

    await Api.axiosInstance.delete<Shutdown>(path)
  },
  addStaticPartDocuments: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId,
    staticPartId,
    documents
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
    staticPartId: number
    documents: File[]
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/static-parts/${staticPartId}/documents`

    const formData = new FormData()
    documents.forEach((document) => formData.append('documents', document))

    await Api.axiosInstance.post<void>(path, formData, {
      headers: {
        'content-type': 'multipart/form-data'
      }
    })
  },
  deleteStaticPartDocument: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId,
    staticPartId,
    documentId
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
    staticPartId: number
    documentId: number
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/static-parts/${staticPartId}/documents/${documentId}`

    await Api.axiosInstance.delete<void>(path)
  },
  generateReport: async (payload: MateriaInstallationReportRequest) => {
    const path = '/plants/refractory-planner/kilns/report'
    const response = await Api.axiosInstance.post<MateriaInstallationReportResponse>(path, payload)
    return response.data
  },
  generateReportExcel: async (payload: MateriaInstallationReportRequest) => {
    const path = '/plants/refractory-planner/kilns/report/excel'
    const response = await Api.axiosInstance.post(path, payload, {responseType: 'arraybuffer'})
    return new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
  }
}
